import type { RuntimeConfig } from "@umijs/max";
import { formatMessage, history } from "@umijs/max";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Fragment, Suspense, type ReactNode } from "react";
import { DEFAULT_LOCALE } from "@airange/shared/i18n-exam-admin";
import { logAirangeBuildInfo } from "@airange/shared/browser-build-info";
import { OIDC_CALLBACK_PATH } from "@airange/shared/oidc-routes";
import "antd/dist/reset.css";

import defaultSettings from "@/app/default-settings";
import { AdminLocaleSwitcher } from "./components/AdminLocaleSwitcher";
import {
  getCurrentUser,
  getUserPreferences,
  logout,
  updateUserPreferences,
  type UserPreferenceRead,
} from "./lib/auth-api";
import { notifyAdminError, notifyAdminWarning } from "./lib/antd-message-bridge";
import { getErrorMessage, isRetryableRequestError } from "./lib/api";
import { initializeAdminLocale, switchAdminLocale } from "./lib/locale";
import { applyPublicBranding } from "./lib/public-branding";
import { clearSession, clearStoredAuthSource, getStoredAuthSource, hasPortalAccess, isAuthenticated, setStoredUser } from "./lib/session";
import { getPublicSystemConfig, type PublicSystemConfig } from "./lib/system-api";
import type { UserSession } from "./lib/auth-api";
import {
  type AdminLayoutSettings,
  type BreadcrumbCrumb,
  type InitialState,
  type NavThemePreference,
  buildCanonicalBreadcrumbs,
  changePasswordPath,
  dashboardPath,
  disableMenuTooltip,
  getBreadcrumbGroup,
  getSystemPrefersDark,
  groupAdminMenuData,
  loginPath,
  resolveNavTheme,
  toPreferenceSettings,
  translateMenuData,
} from "./app-shell/layout-utils";
import {
  AdminLocaleRoot,
  AdminLocaleSync,
  type AdminLocaleSyncProps,
  AdminRootProviders,
  AdminRuntimeBoundary,
  AdminThemeProvider,
  AvatarDropdown,
  LazyDebugSettingDrawer,
  buildUnAccessibleNode,
  normalizeNavThemeSetting,
  toDebugDrawerSettings,
} from "./app-shell/runtime-components";
import "./styles.css";

logAirangeBuildInfo(__AIRANGE_BUILD_INFO__);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 10 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => failureCount < 2 && isRetryableRequestError(error),
    },
    mutations: {
      retry: false,
    },
  },
});

export async function getInitialState(): Promise<InitialState> {
  const localeVersion = initializeAdminLocale();
  let publicConfig: PublicSystemConfig | null = null;
  let preference: UserPreferenceRead | null = null;
  let currentUser: UserSession | null = null;

  try {
    publicConfig = await getPublicSystemConfig();
  } catch {
    publicConfig = null;
  }
  if (isAuthenticated()) {
    const authSource = getStoredAuthSource();
    try {
      const nextUser = await getCurrentUser(authSource);
      if (!hasPortalAccess(nextUser.role_keys)) {
        clearSession();
        clearStoredAuthSource();
      } else {
        currentUser = nextUser;
        setStoredUser(nextUser);
        try {
          const nextPreference = await getUserPreferences();
          preference = nextPreference;
          if (nextPreference.locale) {
            switchAdminLocale(nextPreference.locale);
          }
        } catch {
          preference = null;
        }
      }
    } catch {
      clearSession();
      clearStoredAuthSource();
      currentUser = null;
      preference = null;
    }
  }

  applyPublicBranding({ faviconUrl: publicConfig?.favicon_url });

  const mergedSettings: AdminLayoutSettings = {
    ...defaultSettings,
    ...(preference?.settings ?? {}),
    navTheme: preference?.settings?.navTheme ?? (defaultSettings.navTheme as "light" | "realDark" | undefined) ?? "light",
    title: publicConfig?.platform_name || formatMessage({ id: "common.brand.appName" }),
  };

  return {
    currentUser,
    publicConfig,
    localeVersion: preference?.locale || localeVersion,
    settings: mergedSettings,
  };
}

export function rootContainer(container: ReactNode) {
  return (
    <QueryClientProvider client={queryClient}>
      <AdminRootProviders>
        <AdminLocaleRoot>{container}</AdminLocaleRoot>
      </AdminRootProviders>
    </QueryClientProvider>
  );
}

function readCurrentPathname(): string {
  if (typeof window !== "undefined") {
    return window.location.pathname || "/";
  }
  return "/";
}

export const layout: RuntimeConfig["layout"] = ({ initialState, setInitialState }) => {
  const showDebugSettingDrawer = process.env.NODE_ENV !== "production";
  const navThemeSetting: NavThemePreference = normalizeNavThemeSetting(initialState as InitialState | undefined);
  const effectiveNavTheme = resolveNavTheme(navThemeSetting, getSystemPrefersDark());
  const isDarkTheme = effectiveNavTheme === "realDark";

  const handleLogout = () => {
    void logout(getStoredAuthSource()).catch((error) => {
      notifyAdminWarning(getErrorMessage(error));
    });
    clearSession();
    clearStoredAuthSource();
    void setInitialState((state) => ({
      ...(state ?? { settings: {}, currentUser: null, publicConfig: null, localeVersion: initialState?.localeVersion ?? DEFAULT_LOCALE }),
      currentUser: null,
    }));
    history.push(loginPath);
  };

  const currentUser = initialState?.currentUser;
  const layoutTitle = initialState?.publicConfig?.platform_name || formatMessage({ id: "common.brand.appName" });
  const { navTheme: _rawNavTheme, ...layoutSettingsWithoutNavTheme } = initialState?.settings ?? {};
  const applyPreference = (preference: UserPreferenceRead) => {
    const resolvedLocale = preference.locale || DEFAULT_LOCALE;
    switchAdminLocale(resolvedLocale);
    void setInitialState((state) => ({
      ...(state ?? { settings: {}, currentUser: null, publicConfig: null, localeVersion: resolvedLocale }),
      localeVersion: resolvedLocale,
      settings: {
        ...(state?.settings ?? defaultSettings),
        ...preference.settings,
        title: state?.publicConfig?.platform_name || formatMessage({ id: "common.brand.appName" }),
        navTheme: preference.settings.navTheme ?? "system",
      },
    }));
  };

  return {
    menu: {
      locale: false,
    },
    menuDataRender: (menuData) => translateMenuData(disableMenuTooltip(groupAdminMenuData(menuData))),
    postMenuData: (menuData) => disableMenuTooltip(groupAdminMenuData(menuData ?? [])),
    breadcrumbRender: (routers: BreadcrumbCrumb[] = []) => {
      const pathname = readCurrentPathname();
      const groupLabel = getBreadcrumbGroup(pathname);
      if (routers.length === 0) {
        return routers;
      }

      let nextRouters: BreadcrumbCrumb[] = routers;
      if (groupLabel) {
        const hasGroup = routers.some((item) => item.path === "__group__");
        if (!hasGroup) {
          const [first, ...rest] = routers;
          const groupCrumb = { path: "__group__", breadcrumbName: groupLabel };
          nextRouters = first?.path === "/" ? [first, groupCrumb, ...rest] : [groupCrumb, ...routers];
        }
      }

      return buildCanonicalBreadcrumbs(pathname, nextRouters);
    },
    itemRender: (route, _params, routes, paths) => {
      const isLast = routes.indexOf(route) === routes.length - 1;
      const label = route.breadcrumbName;
      if (!route.path || route.path === "__group__") {
        return <span>{label}</span>;
      }
      if (isLast) {
        return <span>{label}</span>;
      }
      const routePath = String(route.path || "");
      const targetPath =
        routePath.startsWith("/") && !routePath.includes(":")
          ? routePath
          :
              "/" +
              paths
                .filter((segment) => segment && segment !== "__group__")
                .map((segment) => (segment.startsWith("/") ? segment.slice(1) : segment))
                .join("/");
      return <span onClick={() => history.push(targetPath)}>{label}</span>;
    },
    siderWidth: 215,
    token: {
      bgLayout: isDarkTheme ? "linear-gradient(#141414, #0f172a 28%)" : "linear-gradient(#ffffff, hsl(220,23%,97%) 28%)",
      header: {
        heightLayoutHeader: 56,
      },
      sider: {
        paddingInlineLayoutMenu: 8,
        paddingBlockLayoutMenu: 0,
      },
      pageContainer: {
        paddingInlinePageContainerContent: 24,
        paddingBlockPageContainerContent: 16,
        colorBgPageContainer: "transparent",
        colorBgPageContainerFixed: isDarkTheme ? "rgba(15, 23, 42, 0.72)" : "rgba(255, 255, 255, 0.72)",
      },
    },
    logo: initialState?.publicConfig?.logo_url || undefined,
    menuFooterRender: false,
    unAccessible: buildUnAccessibleNode(),
    avatarProps: currentUser
      ? {
          title: currentUser.full_name || currentUser.username,
          render: (_, avatarChildren) => (
            <AvatarDropdown
              currentUser={currentUser}
              settings={(initialState?.settings as AdminLayoutSettings) ?? defaultSettings}
              localeVersion={initialState?.localeVersion ?? DEFAULT_LOCALE}
              onPreferenceApplied={applyPreference}
              onChangePassword={() => history.push(changePasswordPath)}
              onLogout={handleLogout}
            >
              {avatarChildren}
            </AvatarDropdown>
          ),
        }
      : undefined,
    actionsRender: () => [<Fragment key="admin-locale"><AdminLocaleSwitcher /></Fragment>],
    onPageChange: () => {
      const pathname = readCurrentPathname();
      const currentUserOnPage = initialState?.currentUser ?? null;
      const authed = isAuthenticated() && hasPortalAccess(currentUserOnPage?.role_keys);

      if (!authed) {
        if (pathname !== loginPath && pathname !== OIDC_CALLBACK_PATH) {
          history.push(loginPath);
        }
        return;
      }

      if (currentUserOnPage?.must_change_password && pathname !== changePasswordPath) {
        history.push(changePasswordPath);
        return;
      }

      if (!currentUserOnPage?.must_change_password && pathname === changePasswordPath) {
        history.push(dashboardPath);
        return;
      }

      if (pathname === loginPath) {
        history.push(dashboardPath);
      }
    },
    childrenRender: (children) => (
      <AdminLocaleSync publicConfig={initialState?.publicConfig} setInitialState={setInitialState as AdminLocaleSyncProps["setInitialState"]}>
        <AdminThemeProvider navTheme={navThemeSetting}>
          <AdminRuntimeBoundary>
            {children}
            {showDebugSettingDrawer ? (
              <Suspense fallback={null}>
                <LazyDebugSettingDrawer
                  disableUrlParams
                  enableDarkTheme
                  settings={toDebugDrawerSettings(initialState as InitialState | undefined, effectiveNavTheme)}
                  onSettingChange={(settings) => {
                    void updateUserPreferences({
                      settings: toPreferenceSettings(settings),
                      locale: (initialState?.localeVersion || DEFAULT_LOCALE) as UserPreferenceRead["locale"],
                    }).catch((error) => {
                      notifyAdminError(getErrorMessage(error));
                    });
                    void setInitialState((state) => ({
                      ...(state ?? {
                        settings: defaultSettings,
                        currentUser: null,
                        publicConfig: initialState?.publicConfig ?? null,
                        localeVersion: initialState?.localeVersion ?? DEFAULT_LOCALE,
                      }),
                      settings,
                    }));
                  }}
                />
              </Suspense>
            ) : null}
          </AdminRuntimeBoundary>
        </AdminThemeProvider>
      </AdminLocaleSync>
    ),
    headerTitleRender: (logo, title) => (
      <span className="admin-layout-header-title">
        {logo}
        {title}
      </span>
    ),
    ...layoutSettingsWithoutNavTheme,
    navTheme: effectiveNavTheme,
    title: layoutTitle,
  };
};
